import React, { useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import { exportToPDF } from '../../utils/pdfExport'
import { useAuth } from '../../contexts/AuthContext'
import * as FiIcons from 'react-icons/fi'

const { FiDownload, FiFile, FiImage, FiX, FiCheck } = FiIcons

const ExportModal = ({ isOpen, onClose, slides, projectTitle }) => {
  const { profile } = useAuth()
  const [exportType, setExportType] = useState('pdf')
  const [exporting, setExporting] = useState(false)
  const [exportComplete, setExportComplete] = useState(false)
  const [captureMethod, setCaptureMethod] = useState('actual') // 'actual' or 'recreate'

  if (!isOpen) return null

  const exportOptions = [
    {
      id: 'pdf',
      name: 'PDF Document',
      icon: FiFile,
      description: 'Standard PDF for sharing and printing',
      free: true
    },
    {
      id: 'pptx',
      name: 'PowerPoint',
      icon: FiDownload,
      description: 'Editable PowerPoint presentation',
      free: false
    },
    {
      id: 'images',
      name: 'HD Images',
      icon: FiImage,
      description: 'High-quality PNG files for each slide',
      free: false
    }
  ]

  const handleExport = async () => {
    setExporting(true)
    
    try {
      if (exportType === 'pdf') {
        await exportToPDF(slides, projectTitle, captureMethod === 'actual', profile)
      } else {
        // For other export types (PowerPoint, Images), show upgrade prompt
        alert('This export format requires a Pro account. Upgrade now for access to advanced export features!')
        setExporting(false)
        return
      }
      
      setExporting(false)
      setExportComplete(true)
      
      // Reset after a delay
      setTimeout(() => {
        setExportComplete(false)
        onClose()
      }, 2000)
    } catch (error) {
      console.error('Export failed:', error)
      alert('Export failed. Please try again.')
      setExporting(false)
    }
  }



  if (exportComplete) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
        <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Export Complete!</h3>
          <p className="text-gray-600">
            Your presentation has been downloaded successfully.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-2xl p-8 max-w-lg mx-4 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-lg"
        >
          <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
        </button>

        <div className="mb-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Export Presentation</h3>
          <p className="text-gray-600">
            Choose your preferred format to download "{projectTitle}"
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {exportOptions.map((option) => (
            <div
              key={option.id}
              onClick={() => setExportType(option.id)}
              className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                exportType === option.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              } ${!option.free ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  exportType === option.id ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  <SafeIcon 
                    icon={option.icon} 
                    className={`w-5 h-5 ${
                      exportType === option.id ? 'text-primary-600' : 'text-gray-600'
                    }`} 
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-gray-900">{option.name}</h4>
                    {!option.free && (
                      <span className="bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full font-medium">
                        Pro
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </div>
                {exportType === option.id && (
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-600" />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* PDF Capture Method Selection */}
        {exportType === 'pdf' && (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Export Quality</h4>
            <div className="space-y-2">
              <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="captureMethod"
                  value="actual"
                  checked={captureMethod === 'actual'}
                  onChange={(e) => setCaptureMethod(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">High Quality (Recommended)</div>
                  <div className="text-sm text-gray-600">Captures the exact appearance of your slides as displayed in the editor</div>
                </div>
              </label>
              <label className="flex items-start space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="captureMethod"
                  value="recreate"
                  checked={captureMethod === 'recreate'}
                  onChange={(e) => setCaptureMethod(e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-gray-900">Fast Export</div>
                  <div className="text-sm text-gray-600">Recreates slides programmatically - faster but may have minor layout differences</div>
                </div>
              </label>
            </div>
          </div>
        )}

        {!exportOptions.find(opt => opt.id === exportType)?.free && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <p className="text-amber-700 text-sm">
              This export format requires a Pro account.{' '}
              <button className="font-medium underline ml-1">Upgrade now</button>{' '}
              to unlock PowerPoint and HD image exports.
            </p>
          </div>
        )}

        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || !exportOptions.find(opt => opt.id === exportType)?.free}
            className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>Export {exportOptions.find(opt => opt.id === exportType)?.name}</span>
              </>
            )}
          </button>
        </div>

        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Slides: {slides.length} • Estimated size: {Math.ceil(slides.length * 0.5)}MB
          </p>
        </div>
      </div>
    </div>
  )
}

export default ExportModal